/**
 * lectureSdm — la surface de LECTURE du canvas, exprimée dans le modèle
 * documentaire public de SuperDoc (SDM/1, `doc.get()`) et jamais dans l'état
 * ProseMirror de l'éditeur.
 *
 * ## Pourquoi ce module existe
 *
 * Tout ce que le canvas lisait du document passait par `editor.state.doc` :
 * les cinq fonctions d'extraction de `canvasBridge.ts` (paragraphes, format,
 * structure, plage, sélection) et l'augmentation structurelle de
 * `canvasSnapshot.ts` (saut de page, largeur de table) qui alimente
 * `canvas_inspect`. Or `superdoc@2` ne rend plus cet état : sur un runtime v2
 * `editor.commands` vaut `null` et `editor.state` / `editor.view` ne sont plus
 * que des emplacements typés `unknown`, conservés pour les coques v1 (vérifié
 * dans les `.d.ts` publics de `superdoc@2.5.1`,
 * `superdoc/src/core/types/index.d.ts`, interface `Editor`).
 *
 * Sans portage, la sonde de référence ne peut plus lire le document au moment
 * précis où la migration a besoin d'elle pour prouver qu'elle n'a rien cassé.
 *
 * ## Pourquoi SDM/1 et pas `doc.blocks.list()`
 *
 * `blocks.list()` rend une projection PLATE et pauvre : ni italique, ni
 * surlignage, ni saut de page, ni largeur de colonne (vérifié dans
 * `document-api/src/types/blocks.types.d.ts` des deux versions — `BlockListEntry`
 * ne porte que `bold` / `underline` / `alignment` / `styleId`). C'est
 * exactement ce manque qui avait imposé le walk ProseMirror de #929.
 *
 * `doc.get()` rend le document entier dans le modèle canonique **SDM/1** :
 * `SDParagraph` / `SDHeading` avec leurs `SDParagraphProps` (dont
 * `pageBreakBefore`), `SDTable` avec ses `columns[].width` (largeur de grille
 * en POINTS, l'équivalent public de `w:tblGrid`), et des `SDRun` portant
 * `SDRunProps` (bold, italic, underline, highlight). Tout ce que le walk
 * ProseMirror allait chercher, et rien qui vienne du DOM.
 *
 * ⚠️ **Le modèle SDM/1 est identique en 1.44.1 et en 2.5.1** — `sd-nodes.d.ts`
 * et `sd-props.d.ts` des deux paquets ne diffèrent que par leur commentaire de
 * source-map (comparés le 2026-08-14). C'est ce qui rend ce portage prouvable
 * AVANT le changement de version : le code écrit ici tourne contre le runtime
 * que la production fait déjà tourner, et lit la même chose après le bump.
 *
 * ## Unités — deux conversions, aucune devinée
 *
 * SDM exprime les largeurs de colonne en **points** ; le contrat
 * `canvas_inspect` (`CanvasInspectBlockSchema`, `@hector/shared-types`) les
 * publie en **twips** et exige des entiers strictement positifs. 1 pt = 20
 * twips. La forme de la trame est un contrat : elle ne change pas ici.
 *
 * La largeur préférée de la table, elle, sort de SDM en **50e de pourcent** —
 * l'entier OOXML brut, malgré le `kind: 'percent'` de l'union. Ce n'est PAS
 * déduit du nom : la sonde de référence a rougi dessus (5000 observé là où 100
 * était attendu, 2026-08-14) et c'est elle qui a fixé la conversion.
 */

/** Une opération du Document API : synchrone en 1.44.1, promise-capable en V2. */
type LectureBrute = unknown;

function estObjet(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function chaine(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function nombre(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Nœud SDM/1 de contenu, réduit à ce que la lecture du canvas consomme. */
export interface NoeudSdm {
  /** `kind` SDM : `'paragraph' | 'heading' | 'table' | 'list' | …`. */
  kind: string;
  /** Identité du bloc, alignée sur le `nodeId` de `blocks.list()` quand elle existe. */
  id?: string;
  /** Charge utile portée sous la clé homonyme du `kind` (`node.paragraph`, `node.table`…). */
  payload: Record<string, unknown>;
}

/** Marques de run agrégées sur un paragraphe, telles que la trame les publie. */
export interface MarquesDuTexte {
  hasBold: boolean;
  hasItalic: boolean;
  hasUnderline: boolean;
  hasHighlight: boolean;
  /** Un état par run visible — sert les verdicts `allBold` / `allItalic` / `allUnderline`. */
  runs: Array<{ bold: boolean; italic: boolean; underline: boolean }>;
}

/** Ce qu'un paragraphe SDM apporte à la lecture, hors marques. */
export interface ParagrapheSdm {
  /** Index visible : paragraphes et titres uniquement, tables et sections SAUTÉES. */
  index: number;
  noeud: NoeudSdm;
  /** Niveau de titre 1..6, absent pour un paragraphe. */
  niveauDeTitre?: number;
  styleRef?: string;
  alignement?: string;
  texte: string;
  marques: MarquesDuTexte;
}

/**
 * Augmentation structurelle d'un bloc top-level — saut de page et largeur de
 * table. Même forme que ce que `canvas_inspect` publie déjà : ce module change
 * la SOURCE de ces champs, jamais leur forme.
 */
export interface AttributsStructurels {
  /** `kind` SDM du nœud, pour le contrôle de concordance du zip positionnel. */
  nodeType: string;
  pageBreakBefore?: boolean;
  table?: { columnWidthsTwips: number[]; widthPercent: number | null };
}

/** 1 pt = 20 twips (OOXML). */
const TWIPS_PAR_POINT = 20;
/** `w:tblW type="pct"` compte en 50e de pourcent : 5000 = 100 %. */
const POURCENTS_PAR_UNITE_PCT = 50;

/**
 * Lit le corps du document dans le modèle SDM/1.
 *
 * Rend un tableau vide plutôt que de lever quand le provider ne répond pas la
 * forme attendue : cette lecture est un INSTRUMENT (elle augmente une trame
 * déjà utile sans elle), et la faire échouer priverait l'agent de toute
 * l'inspection pour un champ diagnostique. L'absence se lit alors comme
 * « aucun attribut structurel », qui est exactement l'ancien comportement
 * quand l'éditeur n'était pas monté.
 */
export async function lireLeCorpsSdm(
  doc: { get?: (input?: unknown) => LectureBrute } | null | undefined,
): Promise<NoeudSdm[]> {
  if (!doc || typeof doc.get !== 'function') return [];
  const document = await doc.get({});
  if (!estObjet(document) || !Array.isArray(document.body)) return [];
  const out: NoeudSdm[] = [];
  for (const raw of document.body) {
    if (!estObjet(raw)) continue;
    const kind = chaine(raw.kind);
    if (!kind) continue;
    const payload = estObjet(raw[kind]) ? (raw[kind] as Record<string, unknown>) : {};
    out.push({ kind, ...(chaine(raw.id) === undefined ? {} : { id: chaine(raw.id)! }), payload });
  }
  return out;
}

function propsDuNoeud(payload: Record<string, unknown>): Record<string, unknown> {
  return estObjet(payload.props) ? payload.props : {};
}

/**
 * Aplatit les inlines SDM d'un paragraphe en runs de texte.
 *
 * Descend dans les `hyperlink`, qui portent leurs propres `inlines` : un
 * paragraphe dont tout le texte est un lien rendrait sinon une chaîne vide,
 * et l'agent lirait un document amputé.
 */
function collecterLesRuns(inlines: unknown, sortie: Array<{ text: string; props: Record<string, unknown> }>): void {
  if (!Array.isArray(inlines)) return;
  for (const raw of inlines) {
    if (!estObjet(raw)) continue;
    const kind = chaine(raw.kind);
    if (kind === 'run') {
      const run = estObjet(raw.run) ? raw.run : {};
      const text = typeof run.text === 'string' ? run.text : '';
      sortie.push({ text, props: estObjet(run.props) ? run.props : {} });
      continue;
    }
    if (kind === 'hyperlink') {
      const hyperlink = estObjet(raw.hyperlink) ? raw.hyperlink : {};
      collecterLesRuns(hyperlink.inlines, sortie);
    }
    // Les autres inlines (tab, lineBreak, image, field…) ne portent pas de
    // texte adressable par `paragraph_index` — même périmètre qu'avant, où
    // seuls les nœuds `isText` de ProseMirror étaient lus.
  }
}

/**
 * Un soulignement SDM est un OBJET (`{style?, color?}`), pas un booléen :
 * `underline: {}` compte comme souligné (style par défaut `single`), mais
 * `style: 'none'` est un soulignement explicitement retiré.
 */
function estSouligne(props: Record<string, unknown>): boolean {
  const underline = props.underline;
  if (!estObjet(underline)) return false;
  return chaine(underline.style) !== 'none';
}

/** `highlight: 'none'` est un surlignage explicitement retiré, pas un surlignage. */
function estSurligne(props: Record<string, unknown>): boolean {
  const highlight = chaine(props.highlight);
  return highlight !== undefined && highlight !== 'none';
}

function lireLesMarques(runs: Array<{ text: string; props: Record<string, unknown> }>): MarquesDuTexte {
  const etats = runs.map((run) => ({
    bold: run.props.bold === true,
    italic: run.props.italic === true,
    underline: estSouligne(run.props),
  }));
  return {
    hasBold: etats.some((r) => r.bold),
    hasItalic: etats.some((r) => r.italic),
    hasUnderline: etats.some((r) => r.underline),
    hasHighlight: runs.some((run) => estSurligne(run.props)),
    runs: etats,
  };
}

/**
 * Énumère les blocs adressables par `paragraph_index`.
 *
 * FILTRE inchangé depuis le walk ProseMirror : seuls `paragraph` et `heading`
 * comptent. Une table ou une section top-level est SAUTÉE — les compter
 * casserait la sémantique de `paragraph_index`, que l'agent et le harnais de
 * test calculent depuis cette même énumération.
 */
export function lireLesParagraphes(corps: readonly NoeudSdm[]): ParagrapheSdm[] {
  const out: ParagrapheSdm[] = [];
  let index = 0;
  for (const noeud of corps) {
    if (noeud.kind !== 'paragraph' && noeud.kind !== 'heading') continue;
    const props = propsDuNoeud(noeud.payload);
    const runs: Array<{ text: string; props: Record<string, unknown> }> = [];
    collecterLesRuns(noeud.payload.inlines, runs);
    out.push({
      index,
      noeud,
      // ⚠️ CHAMP MORT EN 2.6.0 — branche INATTEIGNABLE, suivi [#986].
      // Mesuré le 2026-08-15 : la projection `doc.get()` de la V2 ne rend
      // AUCUN nœud `heading` (un `<h2>` ressort en `kind:'paragraph'`) et
      // aucune propriété — `level` vaut donc toujours `null`. `niveauDeTitre`
      // n'est plus jamais peuplé depuis SDM. Le `headingLevel` que les sondes
      // lisent vient de `doc.blocks.list()`, pas d'ici. Même cause racine que
      // l'identité de paragraphe : la projection s'est appauvrie.
      ...(noeud.kind === 'heading' && nombre(noeud.payload.level) !== undefined
        ? { niveauDeTitre: nombre(noeud.payload.level)! }
        : {}),
      ...(chaine(noeud.payload.styleRef) === undefined ? {} : { styleRef: chaine(noeud.payload.styleRef)! }),
      ...(chaine(props.alignment) === undefined ? {} : { alignement: chaine(props.alignment)! }),
      texte: runs.map((run) => run.text).join(''),
      marques: lireLesMarques(runs),
    });
    index += 1;
  }
  return out;
}

/**
 * Projette les attributs structurels des blocs top-level, dans l'ordre du
 * corps — donc alignés positionnellement sur `doc.blocks.list()`.
 *
 * ⚠️ La largeur publiée est celle de la GRILLE (`columns[].width`), pas celle
 * que le peintre calcule : c'est l'attribut que l'export sérialise en
 * `w:gridCol` et que le layout-engine mesure. `resolvedLayout.columnWidths`
 * existe aussi mais décrit une résolution d'affichage — la sonde asserte ce
 * qui part dans le `.docx`, pas ce que l'écran a calculé.
 */
export function lireLesAttributsStructurels(corps: readonly NoeudSdm[]): AttributsStructurels[] {
  return corps.map((noeud) => {
    const props = propsDuNoeud(noeud.payload);
    // ⚠️⚠️ CHAMP MORT EN 2.6.0, ET IL MENT À L'AGENT — suivi [#986].
    // Mesuré le 2026-08-15 sur cinq surfaces de lecture publiques (`doc.get`
    // nu, avec `includeResolved`, avec `includeProvenance`, `doc.find` avec
    // `includeContext`, `doc.getNode(<BlockNodeAddress>)`) : AUCUNE ne rend les
    // propriétés d'un paragraphe. `props` est donc toujours vide, et cette
    // valeur vaut **toujours `false`** — y compris sur un paragraphe qui porte
    // réellement un saut de page (`setFlowOptions` rend `success:true` et le
    // peintre fabrique bien une page de plus, mesuré 1 → 2).
    //
    // Ce n'est pas un champ diagnostique dormant : `canvas_inspect` le publie
    // et **l'agent le lit** — un signal ABSENT lui est donc présenté comme un
    // signal NÉGATIF. Ne pas conclure « pas de saut de page » de ce `false`, et
    // ne pas s'en servir pour décider quoi que ce soit tant que #986 n'a pas
    // rendu son identité à la projection. Les deux sondes de référence jugent
    // désormais le saut sur la PAGINATION PEINTE, pas sur ce champ.
    const pageBreakBefore = props.pageBreakBefore === true;

    let table: AttributsStructurels['table'];
    if (noeud.kind === 'table') {
      const colonnes = Array.isArray(noeud.payload.columns) ? noeud.payload.columns : [];
      // `CanvasInspectBlockSchema.table` exige `z.number().int().positive()` par
      // colonne : une largeur flottante ou nulle fait rejeter le receipt ENTIER
      // (`CANVAS_INSPECT_RECEIPT_INVALID`), pas seulement ce champ diagnostique.
      // Le producteur doit donc produire conforme au strict.
      const columnWidthsTwips = colonnes
        .map((colonne) => (estObjet(colonne) ? nombre(colonne.width) : undefined))
        .filter((width): width is number => typeof width === 'number')
        .map((widthPt) => Math.round(widthPt * TWIPS_PAR_POINT))
        .filter((widthTwips) => widthTwips > 0);
      const largeur = estObjet(props.width) ? props.width : {};
      // ⚠️ MESURÉ, pas déduit du nom (sonde ① rouge le 2026-08-14, valeur
      // observée 5000 là où 100 était attendu) : `SDTableWidth` a beau
      // discriminer sur `kind: 'percent'`, sa `value` reste l'entier OOXML brut
      // de `w:tblW type="pct"`, exprimé en **50e de pourcent** — 5000 = 100 %.
      // La projection SDM ne convertit pas cette unité.
      const pourcentBrut = chaine(largeur.kind) === 'percent' ? nombre(largeur.value) : undefined;
      // Le schéma exige `positive().nullable()` : une table sans largeur pct
      // déclarée doit devenir `null`, jamais `0`, sous peine de faire rejeter
      // le receipt entier.
      const widthPercent =
        pourcentBrut !== undefined && pourcentBrut > 0 ? pourcentBrut / POURCENTS_PAR_UNITE_PCT : null;
      table = { columnWidthsTwips, widthPercent };
    }

    return {
      nodeType: noeud.kind,
      ...(pageBreakBefore ? { pageBreakBefore: true } : {}),
      ...(table ? { table } : {}),
    };
  });
}
